import { createFileRoute } from "@tanstack/react-router";
import { PageHeader } from "@/components/page-header";
import { useTitle } from "@/lib/title";

// Every signed-in person can read the terms; there is no permission gate.

export const Route = createFileRoute("/_app/terms")({
  component: TermsRoute,
});

const EFFECTIVE = "October 21, 2026";
const OWNER = "Digital King INC";
const CONTACT = "info@digitalkinginc.com";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h2 className="font-heading text-sm font-bold text-foreground">{title}</h2>
      <div className="mt-1.5 space-y-2 text-sm leading-relaxed text-muted">{children}</div>
    </section>
  );
}

function List({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-1 pl-5">
      {items.map((t) => (
        <li key={t}>{t}</li>
      ))}
    </ul>
  );
}

function TermsRoute() {
  useTitle("Terms of service");

  return (
    <div className="page-shell-narrow">
      <PageHeader title="Terms of service" />
      <div className="rounded-xl border border-border bg-surface p-5 @3xl:p-8">
        <p className="text-xs text-muted">Effective {EFFECTIVE}</p>
        <p className="mt-3 text-sm leading-relaxed text-muted">
          This dashboard (the &ldquo;Service&rdquo;) is provided by {OWNER} (&ldquo;Digital King&rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;). These terms apply to the
          business the Service is provided to (the &ldquo;Client&rdquo;), to every person who signs in, and to anyone who is shown the Service in
          any form (together, &ldquo;you&rdquo;). By signing in, viewing or using the Service you accept these terms. If you do not accept them,
          do not use the Service.
        </p>

        <div className="mt-6 space-y-6">
          <Section title="1. Ownership">
            <p>
              The Service and everything in it belong to Digital King. This includes, without limitation, the software, the source code and
              compiled code, the architecture, the data models, the database structure, the synchronisation and attribution logic, the
              calculations and methods behind every figure, the reports and the way they are organised, the visual design, the layouts, the
              navigation, the colour and type choices, the icons, the copy, the documentation, the configuration, and all know-how, trade
              secrets and other intellectual property embodied in or used to build the Service (together, the &ldquo;Materials&rdquo;).
            </p>
            <p>
              All rights, title and interest in the Materials, including all copyright, trade secret, trademark and other intellectual
              property rights, are and remain the exclusive property of Digital King. Nothing in these terms, in any proposal, invoice or
              services agreement, or in the Client&rsquo;s payment for services transfers any ownership of the Materials to the Client or to
              anyone else. All rights not expressly granted are reserved.
            </p>
          </Section>

          <Section title="2. Your licence">
            <p>
              Digital King grants the Client a limited, personal, non-exclusive, non-transferable, non-sublicensable and revocable licence to
              access the Service, through the accounts Digital King issues, solely to view and use reports about the Client&rsquo;s own
              business, for the Client&rsquo;s own internal purposes, while the Client remains in good standing with Digital King. No other
              right or licence is granted, by implication, estoppel or otherwise.
            </p>
          </Section>

          <Section title="3. What you may not do">
            <p>You may not, and may not permit or help anyone else to:</p>
            <List
              items={[
                "copy, reproduce, duplicate, download, print or store any part of the Materials, other than viewing reports in the normal course of using the Service;",
                "modify, adapt, translate, or create derivative works of any part of the Materials;",
                "reverse engineer, decompile, disassemble, decode, or otherwise attempt to derive or reconstruct the source code, the data models, the logic, the calculations or the methods behind the Service;",
                "study, document, describe or analyse the Service, its design, its reasoning or its workings for the purpose of building, specifying, or helping anyone build anything similar;",
                "use the Materials, or any description or recording of them, to design, build, train, prompt or improve any product, service, tool, model or system, including any artificial intelligence or machine learning system;",
                "take screenshots, screen recordings, exports or notes of the Service in order to reproduce, imitate or share its design, structure or methods;",
                "sell, rent, lease, lend, license, sublicense, distribute, publish, host, frame, mirror or otherwise make the Service or the Materials available to any third party;",
                "share, transfer or disclose sign-in credentials, or allow anyone other than the person an account was issued to use it;",
                "access the Service by automated means, including scraping, crawling, scripts or bots, or probe, scan or test its security;",
                "remove, obscure or alter any proprietary notice, branding or attribution in the Service;",
                "use the Service to build, benchmark against, or support a competing product or service, or to assist a competitor of Digital King; or",
                "use the Service for any unlawful purpose or in breach of these terms.",
              ]}
            />
          </Section>

          <Section title="4. Confidentiality">
            <p>
              The Materials, the existence and content of the Service&rsquo;s design and logic, and any non-public information you learn
              about how the Service is built or how it works are the confidential information and trade secrets of Digital King
              (&ldquo;Confidential Information&rdquo;). You must keep Confidential Information strictly confidential, use it only to use the
              Service as permitted here, and protect it with at least the care you use for your own confidential information and never less
              than reasonable care.
            </p>
            <p>
              Showing the Service to anyone, including employees, contractors, advisers, investors, partners, vendors, agencies or developers,
              does not release them from these terms. The Client is responsible for every person it gives access to or shows the Service to,
              must make sure each of them is bound by obligations at least as protective as these terms before they see it, and is liable for
              any breach by them as if it were the Client&rsquo;s own. Anyone who sees the Service without accepting these terms must stop
              viewing it and must not use or disclose anything they saw.
            </p>
            <p>
              The reports themselves describe the Client&rsquo;s own business, and the Client may use the figures in them in the ordinary
              course of running that business. That use does not extend to the Materials, the design, the structure or the methods behind the
              figures.
            </p>
            <p>
              These confidentiality obligations last for as long as the information remains confidential, and in any case for no less than
              five years after your access ends. Obligations concerning trade secrets last for as long as the information is a trade secret.
            </p>
          </Section>

          <Section title="5. Accounts and access">
            <p>
              Accounts are issued by Digital King to named people and are personal to them. You are responsible for keeping your credentials
              private and for everything done through your account. Tell Digital King at once if you believe an account has been compromised.
              Digital King may suspend, limit or end any account or the Service at any time, with or without notice, including where it
              reasonably believes these terms have been breached. Actions in the Service may be logged, and Digital King may review those logs.
            </p>
          </Section>

          <Section title="6. The Client&rsquo;s data">
            <p>
              Orders, products, advertising, analytics and shopper activity shown in the Service come from the Client&rsquo;s own connected
              accounts and from the Client&rsquo;s online store. That underlying business data belongs to the Client. The Client grants Digital
              King the right to collect, store, process and display it to provide, maintain, secure and improve the Service. The connected
              platforms (such as Shopify, Google and Meta) have their own terms, which govern the data they provide. Shopper tracking in the
              Service is designed to record browsing activity without names, email addresses, phone numbers, postal addresses or IP addresses.
            </p>
          </Section>

          <Section title="7. No warranty">
            <p>
              The Service is provided &ldquo;as is&rdquo; and &ldquo;as available&rdquo;. Figures are drawn from third-party platforms and
              are reconciled and attributed by methods Digital King chooses. They are for information only, may differ from the figures those
              platforms show, may be delayed, and may change as data is corrected. To the fullest extent permitted by law, Digital King disclaims
              all warranties, express or implied, including any warranty of merchantability, fitness for a particular purpose, accuracy, title
              and non-infringement. Business decisions you make in reliance on the Service are your own.
            </p>
          </Section>

          <Section title="8. Limitation of liability">
            <p>
              To the fullest extent permitted by law, Digital King will not be liable for any indirect, incidental, special, consequential,
              exemplary or punitive damages, or for any loss of profit, revenue, business, goodwill or data, arising out of or in connection
              with the Service or these terms, however caused and under any theory of liability, even if advised of the possibility. Digital
              King&rsquo;s total liability for all claims relating to the Service will not exceed the amount the Client paid Digital King for the
              Service in the three months before the event giving rise to the claim.
            </p>
          </Section>

          <Section title="9. Remedies">
            <p>
              You agree that a breach of sections 1, 3 or 4 would cause Digital King harm that money alone cannot repair. Digital King is
              therefore entitled to injunctive or other equitable relief to stop or prevent such a breach, without posting a bond and in
              addition to every other remedy available to it. You will reimburse Digital King for the reasonable costs, including attorneys&rsquo;
              fees, of enforcing these terms against you.
            </p>
          </Section>

          <Section title="10. Feedback">
            <p>
              If you give Digital King suggestions, ideas or other feedback about the Service, Digital King may use them without restriction or
              payment, and they do not give you any rights in the Service.
            </p>
          </Section>

          <Section title="11. Term and termination">
            <p>
              These terms apply from the first time you access the Service and continue until your access ends. Digital King may end the
              Client&rsquo;s or any person&rsquo;s access at any time. When access ends you must stop using the Service and must not keep,
              use or disclose any Materials or Confidential Information. Sections 1, 3, 4, 7, 8, 9, 10, 11, 12 and 13 survive the end of
              these terms.
            </p>
          </Section>

          <Section title="12. General">
            <p>
              These terms are governed by the laws of the State of New Jersey, without regard to its conflict of laws rules, and the state and
              federal courts located in New Jersey have exclusive jurisdiction over any dispute relating to them, except that Digital King may
              seek injunctive relief in any court of competent jurisdiction. Where a written services agreement between Digital King and the
              Client covers the same subject, that agreement governs any conflict, and these terms otherwise supplement it. If any part of these
              terms is held unenforceable, the rest stays in force and the unenforceable part is enforced to the fullest extent allowed. Digital
              King&rsquo;s failure to enforce a term is not a waiver of it. You may not assign these terms. Digital King may assign them to a
              successor of its business. Digital King may update these terms by posting a revised version in the Service with a new effective
              date, and continued use after that date is acceptance of the revised terms.
            </p>
          </Section>

          <Section title="13. Contact">
            <p>Questions about these terms, and requests for permission to do anything they do not allow, go to Digital King at {CONTACT}.</p>
          </Section>
        </div>

        <p className="mt-8 border-t border-border pt-4 text-xs text-muted">
          &copy; {new Date().getFullYear()} {OWNER}. All rights reserved. The Service and the Materials are protected by copyright, trade
          secret and other intellectual property laws.
        </p>
      </div>
    </div>
  );
}
